import { useLayoutEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

const DEFAULT_STORAGE_KEY = "mincool.routeScrollPositions.v1";
const MAX_SAVED_POSITIONS = 100;
const RECENT_SAVE_SKIP_MS = 500;
let cancelPendingTransitionPreservation = null;

function canUseBrowserScroll() {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function isDocumentReload() {
  if (!canUseBrowserScroll() || typeof performance === "undefined") {
    return false;
  }

  return performance.getEntriesByType("navigation")[0]?.type === "reload";
}

function routeKey(location) {
  return location.key || `${location.pathname}${location.search}${location.hash}`;
}

function readPositions(storageKey) {
  if (!canUseBrowserScroll()) {
    return {};
  }

  try {
    const raw = window.sessionStorage.getItem(storageKey);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writePositions(storageKey, positions) {
  if (!canUseBrowserScroll()) {
    return;
  }

  try {
    window.sessionStorage.setItem(storageKey, JSON.stringify(positions));
  } catch {
    // Scroll restoration is progressive enhancement; storage can be disabled.
  }
}

function savePosition(storageKey, key, { skipRecent = false } = {}) {
  const positions = readPositions(storageKey);
  const previous = positions[key];
  if (
    skipRecent
    && Number.isFinite(previous?.updatedAt)
    && Date.now() - previous.updatedAt < RECENT_SAVE_SKIP_MS
  ) {
    return;
  }

  positions[key] = {
    left: window.scrollX,
    top: window.scrollY,
    updatedAt: Date.now(),
  };

  const entries = Object.entries(positions)
    .sort(([, a], [, b]) => (b.updatedAt || 0) - (a.updatedAt || 0))
    .slice(0, MAX_SAVED_POSITIONS);
  writePositions(storageKey, Object.fromEntries(entries));
}

function getHashTarget(hash) {
  if (!hash) {
    return null;
  }

  let id = "";
  try {
    id = decodeURIComponent(hash.slice(1));
  } catch {
    id = hash.slice(1);
  }
  return document.getElementById(id);
}

function restorePosition(storageKey, key, navigationType, { resetOnReload, hash }) {
  const positions = readPositions(storageKey);
  const resetForReload = resetOnReload && !hash && isDocumentReload();
  const position = navigationType === "POP" && !resetForReload ? positions[key] : null;
  if (!position && hash) {
    const target = getHashTarget(hash);
    if (target) {
      target.scrollIntoView({ block: "start", behavior: "auto" });
      return;
    }
  }
  window.scrollTo({
    left: Number.isFinite(position?.left) ? position.left : 0,
    top: Number.isFinite(position?.top) ? position.top : 0,
    behavior: "auto",
  });
}

export function preserveRouteScrollThroughTransition({ settleMs = 0, position: requestedPosition } = {}) {
  if (!canUseBrowserScroll()) return;

  cancelPendingTransitionPreservation?.();
  const position = {
    left: Number.isFinite(requestedPosition?.left) ? requestedPosition.left : window.scrollX,
    top: Number.isFinite(requestedPosition?.top) ? requestedPosition.top : window.scrollY,
  };
  const cancelEvents = ["wheel", "keydown", "pointerdown"];
  let cancelled = false;
  let frame = null;
  let timer = null;
  let touchOrigin = null;

  const cleanup = () => {
    cancelEvents.forEach((eventName) => window.removeEventListener(eventName, cancel));
    window.removeEventListener("touchstart", rememberTouchOrigin);
    window.removeEventListener("touchmove", cancelOnTouchMove);
    if (cancelPendingTransitionPreservation === cancel) cancelPendingTransitionPreservation = null;
  };
  const cancel = () => {
    cancelled = true;
    if (frame !== null) window.cancelAnimationFrame(frame);
    if (timer !== null) window.clearTimeout(timer);
    cleanup();
  };
  const rememberTouchOrigin = (event) => {
    const touch = event.touches?.[0];
    touchOrigin = touch ? { x: touch.clientX, y: touch.clientY } : null;
  };
  const cancelOnTouchMove = (event) => {
    const touch = event.touches?.[0];
    if (!touch || !touchOrigin) return;
    if (Math.hypot(touch.clientX - touchOrigin.x, touch.clientY - touchOrigin.y) >= 8) cancel();
  };
  const restore = () => {
    if (!cancelled) window.scrollTo({ ...position, behavior: "auto" });
  };

  cancelEvents.forEach((eventName) => window.addEventListener(eventName, cancel, { passive: true }));
  window.addEventListener("touchstart", rememberTouchOrigin, { passive: true });
  window.addEventListener("touchmove", cancelOnTouchMove, { passive: true });
  frame = window.requestAnimationFrame(() => {
    restore();
    if (settleMs <= 0) cleanup();
  });
  if (settleMs > 0) {
    timer = window.setTimeout(() => {
      restore();
      cleanup();
    }, Math.min(settleMs, 1_000));
  }
  cancelPendingTransitionPreservation = cancel;
}

export function useRouteScrollRestoration({
  storageKey = DEFAULT_STORAGE_KEY,
  resetOnReload = false,
} = {}) {
  const location = useLocation();
  const navigationType = useNavigationType();

  useLayoutEffect(() => {
    if (!canUseBrowserScroll() || !window.history) {
      return undefined;
    }

    const previousScrollRestoration = window.history.scrollRestoration;
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    return () => {
      if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = previousScrollRestoration;
      }
    };
  }, []);

  useLayoutEffect(() => {
    if (!canUseBrowserScroll()) {
      return undefined;
    }

    const key = routeKey(location);
    const saveCurrentPosition = () => savePosition(storageKey, key);
    const frame = window.requestAnimationFrame(() => {
      if (navigationType !== "POP" && location.state?.preserveScroll) return;
      restorePosition(storageKey, key, navigationType, {
        resetOnReload,
        hash: location.hash,
      });
    });
    document.addEventListener("click", saveCurrentPosition, { capture: true });
    window.addEventListener("pagehide", saveCurrentPosition);

    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener("click", saveCurrentPosition, { capture: true });
      window.removeEventListener("pagehide", saveCurrentPosition);
      savePosition(storageKey, key, { skipRecent: true });
    };
  }, [location, navigationType, resetOnReload, storageKey]);
}
